import { createSlice } from '@reduxjs/toolkit';

// Map of common country codes to their currencies
const COUNTRY_TO_CURRENCY = {
  'US': 'USD', // United States
  'FR': 'EUR', // France
  'DE': 'EUR', // Germany
  'IT': 'EUR', // Italy
  'ES': 'EUR', // Spain
  'NL': 'EUR', // Netherlands
  'BE': 'EUR', // Belgium
  'PT': 'EUR', // Portugal
  'AT': 'EUR', // Austria
  'IE': 'EUR', // Ireland
  'FI': 'EUR', // Finland
  'GR': 'EUR', // Greece
  'GB': 'GBP', // United Kingdom
  'UK': 'GBP', // United Kingdom alternative code
  'CA': 'CAD', // Canada
  'JP': 'JPY', // Japan
  'CN': 'CNY', // China
  'AU': 'AUD', // Australia
  'NZ': 'NZD', // New Zealand
  'CH': 'CHF', // Switzerland
  'SE': 'SEK', // Sweden
  'DK': 'DKK', // Denmark
  'NO': 'NOK', // Norway
  'IN': 'INR', // India
  'RU': 'RUB', // Russia
  'BR': 'BRL', // Brazil
  'ZA': 'ZAR', // South Africa
  'KR': 'KRW', // South Korea
  'SG': 'SGD', // Singapore
  'HK': 'HKD', // Hong Kong
};

// Common currencies to show in the dropdown
export const COMMON_CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
  { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
];

// Get currency symbol from currency code
export const getCurrencySymbol = (currencyCode) => {
  // Handle null, undefined, or non-string values
  if (!currencyCode || typeof currencyCode !== 'string') {
    return '$'; // Default to USD symbol
  }
  
  // Normalize to uppercase for consistent comparison
  const normalizedCode = currencyCode.toUpperCase();
  
  // Find currency in our common currencies list
  const currency = COMMON_CURRENCIES.find(c => c.code === normalizedCode);
  
  // Return the symbol or default to $ if not found
  return currency ? currency.symbol : '$';
};

// Get currency code from country code
export const getCurrencyFromCountry = (countryCode) => {
  return COUNTRY_TO_CURRENCY[countryCode] || 'USD'; // Default to USD
};

// Initial state
const initialState = {
  // Default currency (USD)
  currency: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar'
  },
  isDetecting: false
};

const currencySlice = createSlice({
  name: 'currency',
  initialState,
  reducers: {
    startCurrencyDetection: (state) => {
      state.isDetecting = true;
    },
    setCurrency: (state, action) => {
      const currency = COMMON_CURRENCIES.find(c => c.code === action.payload) || COMMON_CURRENCIES[0];
      state.currency = currency;
      state.isDetecting = false;
    },
    currencyDetectionFailed: (state) => {
      state.isDetecting = false;
    }
  }
});

export const {
  startCurrencyDetection,
  setCurrency,
  currencyDetectionFailed
} = currencySlice.actions;

// Selectors
export const selectCurrency = (state) => state.currency.currency;
export const selectIsDetectingCurrency = (state) => state.currency.isDetecting;

// Detect currency based on user's country
export const detectCurrency = (countryCode) => (dispatch) => {
  dispatch(startCurrencyDetection());
  
  try {
    const detectedCurrencyCode = getCurrencyFromCountry(countryCode);
    dispatch(setCurrency(detectedCurrencyCode));
    return detectedCurrencyCode;
  } catch (error) {
    console.warn('Currency detection failed:', error);
    dispatch(currencyDetectionFailed());
    dispatch(setCurrency('USD')); // Fall back to USD
    return 'USD';
  }
};

export default currencySlice.reducer; 